import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';

import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Button from '@ecp/common/src/components/button/bo/Button';

import { isManagerValue } from '@bo-recoil/common/user/atom';
import { ContentTitle } from '@bo-components/common/styled';
import Form from '@bo-components/common/form/Form';
import FormField from '@bo-components/common/form/FormField';
import MemberRegPopup from '@bo-pages/site/popups/MemberRegPopup';
import MemberEntryMeanPopup from '../../popups/MemberEntryMeanPopup';
import { TERMINATED } from '../../consts/SiteConst';
import { MEMBER_ENTRY_MEAN_CODE_LIST } from '@bo-const/member/memberConst';

const useYN = (yn) => (yn === 'Y' ? '사용' : '미사용');

const MemberInfo = ({ siteBasicInfo, siteMemberCnt, updateMemberCnt, subsidiaryList }) => {
  const [showRegPopup, setShowRegPopup] = useState(false);
  const [showEntryMeanPopup, setShowEntryMeanPopup] = useState(false);
  const location = useLocation();
  const isManager = useRecoilValue(isManagerValue) && !location.pathname.includes('minor');

  const memberCnt = useMemo(() => {
    const normalCnt = siteMemberCnt.normalMemberCnt || 0;
    const assosicateCnt = siteMemberCnt.associateMemberCnt || 0;
    return `${normalCnt + assosicateCnt}명 (준회원 ${assosicateCnt}명, 정회원 ${normalCnt}명)`;
  }, [siteMemberCnt]);

  const moveTo = () => {
    window.open('/member/member');
  };

  return (
    <>
      <FlexBoxColumn width={'100%'} align-items={'flex-start'}>
        <FlexBox justify-content={'space-between'} width={'100%'}>
          <ContentTitle type={'secondary'} width={'100%'}>
            회원가입환경
          </ContentTitle>
          {isManager && (
            <Button type={'Third'} move onClick={moveTo}>
              회원관리
            </Button>
          )}
        </FlexBox>
        <Spacing bottom={'8px'} />
        <Form>
          <FormField label={'비회원기능'} full>
            <TextBox>{useYN(siteBasicInfo.noMemberUseYn)}</TextBox>
          </FormField>
          <FormField label={'회원가입버튼'} full>
            <TextBox>{useYN(siteBasicInfo.memberEntryBtnYn)}</TextBox>
          </FormField>
          <FormField label={'기업회원여부'} full>
            <TextBox>{useYN(siteBasicInfo.bizpMemberYn)}</TextBox>
          </FormField>
          <FormField label={'인증방식'} full>
            <TextBox>{siteBasicInfo.memberEntryMeanCodeName}</TextBox>
            <Spacing margin={'auto'} />
            {isManager && MEMBER_ENTRY_MEAN_CODE_LIST.includes(siteBasicInfo.memberEntryMeanCode) && (
              <Button
                type={'Third'}
                onClick={() => setShowEntryMeanPopup(true)}
                disabled={siteBasicInfo.siteStatCode === TERMINATED}
              >
                인증정보관리
              </Button>
            )}
            {isManager && (
              <Button
                type={'Third'}
                onClick={() => setShowRegPopup(true)}
                disabled={siteBasicInfo.siteStatCode === TERMINATED}
              >
                회원등록
              </Button>
            )}
          </FormField>
          <FormField label={'회원'} full>
            <TextBox>{memberCnt}</TextBox>
          </FormField>
          <FormField label={'관심분야'}>
            <TextBox>{useYN(siteBasicInfo.memberAdditionalInfoInputYn)}</TextBox>
          </FormField>
          <FormField label={'회원가입시\n성별입력'} labelStyle={{ padding: '8px 14px' }}>
            <TextBox>{useYN(siteBasicInfo.memberGenderInputYn)}</TextBox>
          </FormField>
          <FormField label={'마케팅 동의'} full>
            <TextBox>{useYN(siteBasicInfo.mktingUseYn)}</TextBox>
          </FormField>
        </Form>
      </FlexBoxColumn>
      <MemberRegPopup
        siteNumber={siteBasicInfo.siteNumber}
        open={showRegPopup}
        onClose={() => setShowRegPopup(false)}
        updateMemberCnt={updateMemberCnt}
        entryMeanCode={siteBasicInfo.memberEntryMeanCode}
        subsidiaryList={subsidiaryList}
      />
      <MemberEntryMeanPopup
        open={showEntryMeanPopup}
        onClose={() => setShowEntryMeanPopup(false)}
        subTitle={siteBasicInfo.memberEntryMeanCodeName}
        siteNumber={siteBasicInfo.siteNumber}
        entryMeanCode={siteBasicInfo.memberEntryMeanCode}
        siteTypeCode={siteBasicInfo.siteTypeCode}
        subsidiaryList={subsidiaryList}
      />
    </>
  );
};

export default MemberInfo;

MemberInfo.propTypes = {
  siteMemberCnt: PropTypes.object,
  siteBasicInfo: PropTypes.object,
  subsidiaryList: PropTypes.arrayOf(PropTypes.object),
  updateMemberCnt: PropTypes.func,
};
